import { Transition, TransitionChild } from "@headlessui/react";
import {
  useTranslations,
  cx,
  useToast,
  isErrorCodeOnlyAPIError,
} from "@jugl-web/utils";
import { useState, Fragment } from "react";
import { Button } from "@jugl-web/ui-components";
import { EntityTemplates, useRestApiProvider } from "@jugl-web/rest-api";
import {
  getStorageItem,
  NEW_ENTITY_SESSION_PARAMS,
  ENTITY_ID_KEY,
  saveItemToStorage,
} from "@jugl-web/utils/storage";
import { useNavigation } from "@web-src/modules/navigation/hooks/useNavigation";
import countryToCurrency from "country-to-currency";
import { useParams } from "react-router-dom";
import { useCreateWorkspaceLoader } from "@web-src/modules/entities/components/CreateWorkspaceLoader/useCreateWorkspaceLoader";
import { useFeaturesShowcaseModal } from "@web-src/modules/entities/components/FeaturesShowcaseModal/useFeaturesShowcaseModal";
import { useNavigateToRoleBasedInitialPage } from "@web-src/features/app/hooks/useNavigateToRoleBasedInitialPage";
import { SignUpFlowLayout } from "../SignUpFlowLayout";
import { OptionButton } from "./components/OptionButton/OptionButton";
import { SignUpFlowContentLayout } from "../../components/SignUpFlowContentLayout/SignUpFlowContentLayout";
import { EntityLimitReachedAlert } from "./components/EntityLimitReachedAlert/EntityLimitReachedAlert";

export const SignUpNewWorkspaceSetup = () => {
  const { t } = useTranslations();
  const navigateToRoleBasedInitialPage = useNavigateToRoleBasedInitialPage();
  const { open: openFeaturesShowcaseModal } = useFeaturesShowcaseModal();
  const [purpose, setPurpose] = useState<
    Partial<EntityTemplates["step1_options"]>[number] | null
  >(null);
  const [departments, setDepartments] = useState<
    Set<Partial<EntityTemplates["step2_options"]>[number]>
  >(new Set());

  const { startLoading, cancel: cancelCreateWorkspaceLoader } =
    useCreateWorkspaceLoader();
  const { entitiesApi } = useRestApiProvider();
  const [step, setStep] = useState<"purpose" | "options">("purpose");
  const { toast } = useToast();
  const { navigateToPage } = useNavigation();
  const { data: templates, isLoading } = entitiesApi.useEntityTemplatesQuery();
  const { flow } = useParams();
  const [
    workspaceLimitReachedAlertIsOpen,
    setWorkspaceLimitReachedAlertIsOpen,
  ] = useState(false);
  const [createOrganization] = entitiesApi.useCreateEntityMutation();
  const [isCreateInProgress, setIsCreateInProgress] = useState(false);

  const handleDepartmentsChange = (
    option: EntityTemplates["step2_options"][number]
  ) => {
    setDepartments((prevSet) => {
      const newSet = new Set(prevSet);
      if (newSet.has(option)) {
        newSet.delete(option);
      } else {
        newSet.add(option);
      }
      return newSet;
    });
  };

  const handleSetPurpose = (
    option: EntityTemplates["step1_options"][number]
  ) => {
    setPurpose(option);
    setStep("options");
  };

  const handleSubmit = () => {
    if (isCreateInProgress) {
      return;
    }
    setIsCreateInProgress(true);
    const params:
      | {
          country?: string;
          name?: string;
          email?: string;
        }
      | undefined = getStorageItem(NEW_ENTITY_SESSION_PARAMS, sessionStorage);
    if (!params || !params.name || !params.email || !params.country) {
      navigateToPage("authNewWorkspace", {
        flow: "signup",
      });
      setIsCreateInProgress(false);
      return;
    }
    if (!purpose) {
      setIsCreateInProgress(false);
      return;
    }
    const { setCanFinish } = startLoading();
    createOrganization({
      data: {
        country: params.country,
        name: params.name,
        billing_email: params.email,
        currency:
          countryToCurrency[params.country as keyof typeof countryToCurrency] ||
          "USD",
      },
      template: {
        step1: [purpose] as Partial<EntityTemplates["step1_options"]>,
        step2: Array.from(departments) as EntityTemplates["step2_options"],
      },
    }).then((response) => {
      if ("error" in response) {
        cancelCreateWorkspaceLoader();
        if (isErrorCodeOnlyAPIError(response.error)) {
          if (response.error.data.errors === "max_limit_reached") {
            setWorkspaceLimitReachedAlertIsOpen(true);
          } else {
            toast(
              t({
                id: "feedback.failed-to-create-workspace",
                defaultMessage: "Failed to create a workspace",
              })
            );
          }
        } else {
          toast(
            t({
              id: "feedback.network-error",
              defaultMessage: "Network error",
            })
          );
        }
        setIsCreateInProgress(false);
        return;
      }
      if ("data" in response) {
        setCanFinish();
        saveItemToStorage(ENTITY_ID_KEY, response.data.id);
        saveItemToStorage(NEW_ENTITY_SESSION_PARAMS, undefined, sessionStorage);
        navigateToRoleBasedInitialPage(response.data);
        openFeaturesShowcaseModal();
      }
      setIsCreateInProgress(false);
    });
  };

  return (
    <SignUpFlowLayout
      type="setupWorkspace"
      progressBar={!purpose ? 82 : 97}
      footerClassName="pb-6"
      isLoading={isLoading}
      onBackClick={() => {
        if (purpose && step === "options") {
          setStep("purpose");
        } else {
          navigateToPage("authNewWorkspace", {
            flow: flow === "inapp" ? "inapp" : "signup",
          });
        }
      }}
    >
      <SignUpFlowContentLayout
        titleSectionClassName="max-2xl:pt-[56px]"
        title={
          step === "purpose"
            ? t({
                id: "setup-entity.title",
                defaultMessage: "What do you work on?",
              })
            : ""
        }
        subtitle={
          step === "purpose"
            ? t({
                id: "setup-entity.subtitle",
                defaultMessage:
                  "We’ll match your role with the right setup ✨ ",
              })
            : ""
        }
        description={
          <div
            className={cx(
              "flex w-[514px] flex-col items-center gap-4 pb-[64px]"
            )}
          >
            {step === "purpose" && (
              <>
                <div className="base font-medium text-dark">
                  {t({
                    id: "setup-entity.entity-activity-title",
                    defaultMessage: "What do you want to use Jugl for?",
                  })}
                </div>
                <div className="flex w-[542px] flex-wrap items-center justify-center gap-3 2xl:w-[704px]">
                  {templates?.step1_options.map((option) => (
                    <OptionButton
                      key={option}
                      label={option}
                      onClick={() => handleSetPurpose(option)}
                      isActive={purpose === option}
                    />
                  ))}
                </div>
              </>
            )}

            <Transition show={step === "options"} as={Fragment}>
              <TransitionChild
                as="div"
                enter="transition-all duration-700"
                enterFrom="opacity-0 transform translate-y-40"
                enterTo="opacity-100 transform translate-y-0"
              >
                <>
                  <div
                    className={cx(
                      "base pb-[32px] text-center font-medium text-dark",
                      {
                        "max-2xl:hidden": step === "options",
                      }
                    )}
                  >
                    {t({
                      id: "setup-entity.entity-activity-title",
                      defaultMessage: "What do you want to use Jugl for?",
                    })}
                  </div>
                  <div
                    className={cx(
                      "easy-in-out flex w-[542px] flex-wrap items-center justify-center gap-3 transition-all duration-700 2xl:w-[704px]",
                      {
                        "max-2xl:hidden": step === "options",
                      }
                    )}
                  >
                    {templates?.step1_options.map((option) => (
                      <OptionButton
                        key={option}
                        label={option}
                        onClick={() => handleSetPurpose(option)}
                        isActive={purpose === option}
                      />
                    ))}
                  </div>
                  <div className="base pt-[48px] pb-4 text-center font-medium text-dark">
                    {t({
                      id: "setup-entity.pick-your-department",
                      defaultMessage: "Pick your department",
                    })}
                  </div>
                  <div className="flex w-[542px] flex-wrap items-center justify-center gap-3 2xl:w-[704px]">
                    {templates?.step2_options.map((option) => (
                      <OptionButton
                        key={option}
                        label={option}
                        onClick={() => handleDepartmentsChange(option)}
                        isActive={departments?.has(option)}
                      />
                    ))}
                  </div>
                </>
              </TransitionChild>
            </Transition>
          </div>
        }
        footer={
          (step === "options" && (
            <div className="pb-16">
              <Button
                isDisabled={
                  !departments || !departments?.size || isCreateInProgress
                }
                className="w-[300px] disabled:bg-grey-200 disabled:text-dark-600 disabled:hover:bg-grey-200"
                size="small"
                onClick={handleSubmit}
              >
                {t({
                  id: "common.create-workspace",
                  defaultMessage: "Create Workspace",
                })}
              </Button>
            </div>
          )) || <div className="h-[1px] w-full pt-[200px]" />
        }
        hideTitleSection={step === "options"}
      />
      <EntityLimitReachedAlert
        isOpen={workspaceLimitReachedAlertIsOpen}
        onClose={() => setWorkspaceLimitReachedAlertIsOpen(false)}
      />
    </SignUpFlowLayout>
  );
};
